import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useDialog } from '../../hooks/useDialog'
import { usePagination } from '../../hooks/usePagination'
import catalogService from '../../services/catalogService'
import { Ingredient, IngredientPayload, FoodGroup, Season, Allergen } from '../../types/catalog'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Pagination } from '../../components/ui/Pagination'
import { IngredientForm } from './IngredientForm'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { useConfirm } from '../../hooks/useConfirm'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { IngredientRow } from './IngredientRow'

export function IngredientsPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const dialog = useDialog<Ingredient>()

  const [items, setItems] = useState<Ingredient[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [payload, setPayload] = useState<IngredientPayload | null>(null)

  // Reference data for the form selects
  const [foodGroupsList, setFoodGroupsList] = useState<FoodGroup[]>([])
  const [seasonsList, setSeasonsList] = useState<Season[]>([])
  const [allergensList, setAllergensList] = useState<Allergen[]>([])

  const svc = useMemo(() => catalogService(token), [token])
  const { ingredients, foodGroups, seasons, allergens } = svc

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [ingRes, fgRes, sRes, aRes] = await Promise.all([
        ingredients.list(page, perPage),
        foodGroups.list(1, 100),
        seasons.list(1, 100),
        allergens.list(1, 100),
      ])
      setItems(ingRes.items)
      setTotal(ingRes.total)
      setFoodGroupsList(fgRes.items)
      setSeasonsList(sRes.items)
      setAllergensList(aRes.items)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, ingredients, foodGroups, seasons, allergens])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = useCallback(async () => {
    if (!payload) return
    setSaving(true)
    try {
      if (dialog.data) {
        await svc.ingredients.update(dialog.data.id, payload)
      } else {
        await svc.ingredients.create(payload)
      }
      dialog.close()
      load()
    } finally {
      setSaving(false)
    }
  }, [dialog, payload, svc, load])

  const confirmDialog = useConfirm()

  const handleDelete = useCallback(
    (item: Ingredient) => {
      confirmDialog.confirm(
        'Delete ingredient',
        `Are you sure you want to delete "${item.translations.find(t => t.locale === 'en')?.name}"? This action cannot be undone.`,
        async () => {
          await svc.ingredients.delete(item.id)
          load()
        }
      )
    },
    [confirmDialog, svc, load]
  )

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Ingredients</PageTitle>
        </PageMeta>
        <Button onClick={() => dialog.open()}>+ Add ingredient</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={5} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>English</TH>
                  <TH>Spanish</TH>
                  <TH>Food group</TH>
                  <TH>Seasons</TH>
                  <TH>Allergens</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState message="No ingredients yet." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <IngredientRow
                    key={item.id}
                    item={item}
                    onEdit={dialog.open}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <Dialog
        open={dialog.isOpen}
        title={dialog.data ? 'Edit ingredient' : 'Add ingredient'}
        onClose={dialog.close}
        onConfirm={handleSave}
        confirmLabel={dialog.data ? 'Save changes' : 'Add ingredient'}
        confirmLoading={saving}
        confirmDisabled={!payload?.foodGroupId?.trim()}
        width="580px"
      >
        <IngredientForm
          initial={dialog.data}
          foodGroups={foodGroupsList}
          seasons={seasonsList}
          allergens={allergensList}
          onChange={setPayload}
        />
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Delete"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
